import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, MessageCircle } from "lucide-react";
import Hero from "../components/Hero";
import WhyUs from "../components/WhyUs";
import { siteData } from "../mock";

export default function HomePage() {
  const featured = siteData.properties.filter((p) => p.featured).slice(0, 3);
  const { primaryPhone, primaryPhoneRaw, whatsapp } = siteData.brand;

  return (
    <>
      <Hero />

      {/* Short intro */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-5">WELCOME TO JK ESTATES</div>
          <h2 className="font-serif text-neutral-900 text-[38px] md:text-[56px] leading-[1.08] tracking-tight">
            Tricity&apos;s trusted property partner <span className="italic">since 2009.</span>
          </h2>
          <p className="mt-7 text-neutral-700 text-[16px] md:text-[18px] leading-[1.85] max-w-3xl mx-auto">
            From first-home hunters to industrial investors — JK Estates has helped 200+ families and businesses find the right address across Chandigarh, Mohali and Panchkula. Hand-picked inventory, verified paperwork, end-to-end support.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/properties" className="btn-dark inline-flex items-center gap-2 px-7 py-3.5 text-[13px] tracking-wide">View Properties <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/about" className="btn-outline-dark inline-flex items-center gap-2 px-7 py-3.5 text-[13px] tracking-wide">Our Story</Link>
          </div>
        </div>
      </section>

      <WhyUs />

      {/* Services preview */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-5">WHAT WE DO</div>
              <h2 className="font-serif text-neutral-900 text-[38px] md:text-[56px] leading-[1.05] tracking-tight max-w-2xl">
                Three pillars, <span className="italic">one trusted partner.</span>
              </h2>
            </div>
            <Link to="/services" className="text-[#b88b4a] hover:text-neutral-900 inline-flex items-center gap-2 text-[13.5px] tracking-wide transition-colors">
              Explore all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-7">
            {siteData.services.map((s) => (
              <Link to="/services" key={s.num} className="group block border border-[#e7e2d7] bg-jk-cream hover:bg-white transition-colors">
                <div className="aspect-[4/3] overflow-hidden bg-[#0f1a2e]">
                  {s.image ? (
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="w-full h-full" />
                  )}
                </div>
                <div className="p-7">
                  <div className="text-[11px] tracking-[0.3em] text-[#b88b4a]">{s.num} · {s.kicker}</div>
                  <h3 className="mt-3 font-serif text-neutral-900 text-[26px] leading-tight">{s.title}</h3>
                  <p className="mt-3 text-neutral-700 text-[14.5px] leading-[1.75]">{s.desc}</p>
                  <div className="mt-5 text-[#b88b4a] inline-flex items-center gap-1.5 text-[13px] tracking-wide">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured properties preview */}
      <section className="py-20 md:py-24 bg-jk-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-5">FEATURED PROPERTIES</div>
              <h2 className="font-serif text-neutral-900 text-[38px] md:text-[56px] leading-[1.05] tracking-tight max-w-2xl">
                Tricity&apos;s most wanted <span className="italic">addresses.</span>
              </h2>
            </div>
            <Link to="/properties" className="text-[#b88b4a] hover:text-neutral-900 inline-flex items-center gap-2 text-[13.5px] tracking-wide transition-colors">
              View all listings <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {featured.map((p) => (
              <article key={p.id} className="border border-[#e7e2d7] bg-white flex flex-col">
                <div className="aspect-[4/3] overflow-hidden bg-[#f7f4ee]">
                  <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[12.5px] text-neutral-500">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{p.location}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-neutral-900 text-[24px] leading-[1.15]">{p.title}</h3>
                  <div className="mt-2 text-[11.5px] tracking-[0.22em] text-[#b88b4a]">{p.tag}</div>
                  <p className="mt-4 text-neutral-700 text-[14.5px] leading-[1.75] line-clamp-3">{p.desc}</p>
                  <div className="mt-auto pt-6 grid grid-cols-2 gap-3">
                    <a href={`tel:${primaryPhoneRaw}`} className="btn-dark inline-flex items-center justify-center gap-2 py-3 text-[12.5px] tracking-wide">
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                    <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi JK Estates, I'm interested in "${p.title}" (Ref ${p.id}).`)}`} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex items-center justify-center gap-2 py-3 text-[12.5px] tracking-wide">
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative py-20 md:py-24 bg-jk-navy overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{backgroundImage: "radial-gradient(circle at 20% 20%, #c6a071 1px, transparent 1px)", backgroundSize: "28px 28px"}} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-10 text-center">
          <div className="text-[11px] tracking-[0.32em] text-[#c6a071] mb-5">READY TO START?</div>
          <h2 className="font-serif text-white text-[36px] md:text-[56px] leading-[1.08] tracking-tight">
            Tell us what you&apos;re looking for. <span className="italic text-[#c6a071]">We&apos;ll do the hard work.</span>
          </h2>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-2xl mx-auto">
            <a href={`tel:${primaryPhoneRaw}`} className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-4 text-[13.5px] tracking-wide w-full sm:w-auto font-medium">
              <Phone className="w-4 h-4" /> Call {primaryPhone}
            </a>
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-outline-light inline-flex items-center justify-center gap-2 px-7 py-4 text-[13.5px] tracking-wide w-full sm:w-auto">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <Link to="/contact" className="text-[#c6a071] hover:text-white inline-flex items-center gap-2 px-4 py-4 text-[13.5px] tracking-wide transition-colors">
              Send a message <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
